import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthApi } from '../api/auth.api';
import {
  AuthCredentials,
  ResetPasswordCredentials,
  SetPasswordCredentials,
  TokenVerification,
} from '../models/auth.model';
import { User } from '../models/user.model';
import { ResetPasswordPayloadMapper } from '../api/mappers/reset-password.payload.mapper';
import { SetPasswordPayloadMapper } from '../api/mappers/set-password.payload.mapper';

const INVALID_CREDENTIALS_ERROR = {
  error: 'Invalid credentials',
  message: 'Please check your email and password and try again',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _api = inject(AuthApi);
  private readonly _resetPasswordPayloadMapper =
    new ResetPasswordPayloadMapper();
  private readonly _setPasswordPayloadMapper = new SetPasswordPayloadMapper();

  public login(data: AuthCredentials): Observable<User> {
    return this._api.login(data).pipe(
      catchError((error) => {
        if (error.error?.msg === INVALID_CREDENTIALS_ERROR.error) {
          return throwError(() => INVALID_CREDENTIALS_ERROR);
        }

        return throwError(() => error);
      }),
    );
  }

  public forgotPassword(
    email: string,
    captcha: string | null,
  ): Observable<string> {
    return this._api.forgotPassword(email, captcha);
  }

  public resetPassword(
    userId: number,
    credentials: ResetPasswordCredentials,
  ): Observable<string> {
    const payload = this._resetPasswordPayloadMapper.toPayload(credentials);
    return this._api.resetPassword(userId, payload);
  }

  public setPassword(credentials: SetPasswordCredentials): Observable<string> {
    const payload = this._setPasswordPayloadMapper.toPayload(credentials);
    return this._api.setPassword(credentials.token, payload);
  }

  public verifyToken(token: string): Observable<TokenVerification> {
    return this._api.verifyToken(token).pipe(
      map(({ result }) => ({
        isLinkValid: result.reset_link_valid === 1,
        isPasswordReset: result.password_reset === 1,
        lockoutAttempt: result.lockout_attempt,
      })),
    );
  }

  public logout(userId: number): Observable<void> {
    return this._api.logout(userId);
  }
}
